import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Check, ArrowRight, ChevronDown, ArrowLeftRight, Target, Wallet, Receipt, Sparkles, FileText } from "lucide-react";
import { PLANS } from "@/pages/Pricing";

const HERO_IMG = "https://images.unsplash.com/photo-1780246032682-b2dd1a6291d7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwyfHx2aW50YWdlJTIwbGVkZ2VyJTIwYm9va3xlbnwwfHx8fDE3ODQ2MzE2ODB8MA&ixlib=rb-4.1.0&q=85";

function LandingNav() {
  const links = [
    { href: "#features", label: "Features" },
    { href: "#screenshots", label: "Inside" },
    { href: "#pricing", label: "Pricing" },
    { href: "#faq", label: "FAQ" },
    { href: "#contact", label: "Contact" },
  ];
  return (
    <header className="sticky top-0 z-40 bg-[#F7F5F0] border-b-2 border-[#1A1F16]">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-4 flex items-center justify-between gap-6">
        <h1 className="font-serif text-2xl lg:text-3xl tracking-tight shrink-0">
          Fin<span className="italic text-[#A67C00]">·</span>Ledger
        </h1>
        <nav className="hidden md:flex items-center gap-6">
          {links.map((l) => (
            <a key={l.href} href={l.href} data-testid={`landing-nav-${l.label.toLowerCase()}`} className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6C7565] hover:text-[#1A1F16] transition-colors">
              {l.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-3 shrink-0">
          <Link to="/login" data-testid="landing-signin-btn" className="border border-[#1A1F16] px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] hover:bg-[#1A1F16] hover:text-[#F7F5F0] transition-colors">
            Sign in
          </Link>
          <Link to="/register" data-testid="landing-getstarted-btn" className="bg-[#1A1F16] text-[#F7F5F0] px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] hover:bg-[#A67C00] transition-colors">
            Get started
          </Link>
        </div>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="border-b-2 border-[#1A1F16]">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-3 border-b border-[#D8D4C7] font-mono text-[10px] uppercase tracking-[0.3em] text-[#6C7565] flex justify-between flex-wrap gap-2">
        <span>Est. 2026 · The Household Ledger</span>
        <span>INR Edition · Lakhs & Crores, native</span>
      </div>
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-14 lg:py-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div>
          <h2 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
            Every rupee,<br />on the <span className="italic text-[#A67C00]">record.</span>
          </h2>
          <p className="font-serif text-xl lg:text-2xl mt-8 leading-snug text-[#3C4537] max-w-lg">
            Income, expenses, budgets, bills, demat — one paper-quiet dashboard for everything that moves through your home. With an AI that reads your ledger and a mic that writes to it.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/register" data-testid="hero-start-free-btn" className="bg-[#1A1F16] text-[#F7F5F0] px-8 py-4 text-xs uppercase tracking-[0.25em] hover:bg-[#A67C00] transition-colors inline-flex items-center gap-2">
              Start free <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="#pricing" data-testid="hero-see-pricing-btn" className="border-2 border-[#1A1F16] px-8 py-4 text-xs uppercase tracking-[0.25em] hover:bg-[#1A1F16] hover:text-[#F7F5F0] transition-colors">
              See pricing
            </a>
          </div>
          <p className="mt-6 font-mono text-[10px] uppercase tracking-widest text-[#6C7565]">Free forever plan · No card required</p>
        </div>
        <div className="relative">
          <img src={HERO_IMG} alt="A vintage handwritten ledger book" className="w-full h-[380px] lg:h-[460px] object-cover border-2 border-[#1A1F16] shadow-[8px_8px_0px_#1A1F16]" />
          <div className="absolute -bottom-4 -left-4 bg-[#A67C00] text-white font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-2">
            The old way — beautifully replaced
          </div>
        </div>
      </div>
    </section>
  );
}

const FEATURES = [
  { icon: ArrowLeftRight, title: "Transactions", text: "Log income & expenses with categories, search, date filters and CSV export. Or just speak — the mic writes it down." },
  { icon: Target, title: "Budgets", text: "Set monthly limits per category and watch the pace. AI warns you before the month runs hot." },
  { icon: Wallet, title: "Accounts & Demat", text: "Banks, wallets, cash, credit cards — plus demat holdings with live P/L on every stock." },
  { icon: Receipt, title: "Bills & Autopay", text: "Track due dates, recurring bills, and pay them right inside the app via Razorpay — UPI, cards, netbanking." },
  { icon: Sparkles, title: "AI Assistant", text: "Ask your ledger anything. It answers from your data — and can even record transactions from plain English." },
  { icon: FileText, title: "Reports", text: "Twelve-month cashflow, category trends, income vs expense, AI-written monthly briefs. Print-worthy." },
];

function Features() {
  return (
    <section id="features" className="border-b-2 border-[#1A1F16] bg-white">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-16">
        <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#6C7565]">Section I</span>
        <h3 className="font-serif text-4xl sm:text-5xl tracking-tight mt-2 mb-12">What's in the ledger.</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-[#1A1F16]">
          {FEATURES.map((f) => (
            <div key={f.title} className="border-r border-b border-[#1A1F16] p-8 hover:bg-[#FDFBF7] transition-colors" data-testid={`feature-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <f.icon className="w-5 h-5 text-[#A67C00]" />
              <h4 className="font-serif text-2xl tracking-tight mt-4">{f.title}</h4>
              <p className="text-sm leading-relaxed text-[#3C4537] mt-2">{f.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Screenshots() {
  return (
    <section id="screenshots" className="border-b-2 border-[#1A1F16]">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-16">
        <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#6C7565]">Section II</span>
        <h3 className="font-serif text-4xl sm:text-5xl tracking-tight mt-2 mb-12">A look inside.</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <figure data-testid="screenshot-dashboard">
            <img src="/shots/dashboard.png" alt="FinLedger dashboard with net worth, income, expense and AI spending signals" className="w-full border-2 border-[#1A1F16] shadow-[8px_8px_0px_#1A1F16]" loading="lazy" />
            <figcaption className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-[#6C7565]">The Overview — net worth, savings rate & AI spending signals</figcaption>
          </figure>
          <figure data-testid="screenshot-reports">
            <img src="/shots/reports.png" alt="FinLedger reports with twelve month cashflow and category charts" className="w-full border-2 border-[#1A1F16] shadow-[8px_8px_0px_#A67C00]" loading="lazy" />
            <figcaption className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-[#6C7565]">Reports — cashflow, category trends & AI monthly briefs</figcaption>
          </figure>
        </div>
      </div>
    </section>
  );
}

function PricingSection() {
  const navigate = useNavigate();
  const goBuy = () => navigate("/register?next=/pricing");
  return (
    <section id="pricing" className="border-b-2 border-[#1A1F16] bg-white">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-16">
        <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#6C7565]">Section III · Classifieds</span>
        <h3 className="font-serif text-4xl sm:text-5xl tracking-tight mt-2 mb-2">Pricing.</h3>
        <p className="font-serif text-lg text-[#6C7565] mb-12">Honest rates. No fine print worth squinting at.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {PLANS.map((p) => (
            <div key={p.id} className={`relative border-2 p-8 flex flex-col bg-[#F7F5F0] ${p.badge ? "border-[#A67C00] shadow-[6px_6px_0px_#A67C00]" : "border-[#1A1F16] shadow-[6px_6px_0px_#1A1F16]"}`} data-testid={`landing-plan-${p.id}`}>
              {p.badge && <span className="absolute -top-3 left-6 bg-[#A67C00] text-white font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1">{p.badge}</span>}
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#6C7565]">{p.name}</div>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="font-serif text-5xl tracking-tight">{p.price}</span>
                <span className="font-mono text-[10px] uppercase tracking-widest text-[#6C7565]">{p.period}</span>
              </div>
              <ul className="mt-6 space-y-2.5 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm"><Check className="w-4 h-4 text-[#2E6F40] shrink-0 mt-0.5" /> {f}</li>
                ))}
              </ul>
              <button
                data-testid={`landing-buy-${p.id}`}
                onClick={p.id === "free" ? () => navigate("/register") : goBuy}
                className={`mt-8 w-full py-3 text-xs uppercase tracking-[0.2em] transition-colors ${p.id === "free" ? "border-2 border-[#1A1F16] hover:bg-[#1A1F16] hover:text-[#F7F5F0]" : "bg-[#1A1F16] text-[#F7F5F0] hover:bg-[#A67C00]"}`}
              >
                {p.id === "free" ? "Start free" : "Buy now"}
              </button>
            </div>
          ))}
        </div>
        <p className="mt-8 font-mono text-[10px] uppercase tracking-widest text-[#6C7565]">Payments via Razorpay · UPI, cards & netbanking · Premium activates instantly</p>
      </div>
    </section>
  );
}

// Testimonials intentionally removed until real customer reviews are available.
// (Re-add a Testimonials section here with genuine quotes when ready.)

const FAQS = [
  { q: "Is the Free plan really free forever?", a: "Yes. Transactions, budgets, accounts, bills and basic reports are free with no time limit and no card required. Premium adds the AI assistant, voice logging, AI insights and advanced reports." },
  { q: "How do payments work?", a: "Checkout is powered by Razorpay — UPI, debit/credit cards and netbanking. Your plan activates the moment the payment is verified, and Premium's expiry date shows in your account." },
  { q: "What does the AI actually see?", a: "Only your own ledger — your transactions, budgets, accounts and bills. It answers questions, writes monthly briefs, and can record entries you dictate. Your data is never shared." },
  { q: "Can I pay bills from inside the app?", a: "Yes. Add a bill, hit Pay Now, and settle it via Razorpay. The bill is marked paid and the expense is booked to your ledger automatically." },
  { q: "What happens when my Premium month ends?", a: "You drop back to the Free plan gracefully — no data loss, no lockout. Renew any time, or buy the Lifetime license once and never think about it again." },
];

function FAQ() {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="border-b-2 border-[#1A1F16] bg-white">
      <div className="max-w-[880px] mx-auto px-6 lg:px-10 py-16">
        <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#6C7565]">Section V</span>
        <h3 className="font-serif text-4xl sm:text-5xl tracking-tight mt-2 mb-12">Questions, answered.</h3>
        <div className="border-t-2 border-[#1A1F16]">
          {FAQS.map((f, i) => (
            <div key={f.q} className="border-b border-[#1A1F16]">
              <button
                onClick={() => setOpen(open === i ? -1 : i)}
                data-testid={`faq-toggle-${i}`}
                className="w-full flex items-center justify-between gap-4 py-5 text-left hover:text-[#A67C00] transition-colors"
              >
                <span className="font-serif text-xl lg:text-2xl tracking-tight">{f.q}</span>
                <ChevronDown className={`w-5 h-5 shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`} />
              </button>
              {open === i && <p className="pb-6 text-sm leading-relaxed text-[#3C4537] max-w-2xl" data-testid={`faq-answer-${i}`}>{f.a}</p>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent — we'll get back to you soon");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Could not send message. Please try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <section id="contact">
      <div className="max-w-[880px] mx-auto px-6 lg:px-10 py-16">
        <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#6C7565]">Section VI</span>
        <h3 className="font-serif text-4xl sm:text-5xl tracking-tight mt-2 mb-12">Write to us.</h3>
        <form onSubmit={submit} className="border-2 border-[#1A1F16] bg-white p-8 lg:p-12 shadow-[8px_8px_0px_#1A1F16]" data-testid="contact-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <label className="block">
              <span className="font-mono text-[10px] tracking-widest uppercase text-[#6C7565] block mb-2">Name</span>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name-input" className="w-full bg-transparent border-0 border-b-2 border-[#1A1F16] py-2 focus:outline-none focus:border-[#A67C00]" placeholder="Your name" />
            </label>
            <label className="block">
              <span className="font-mono text-[10px] tracking-widest uppercase text-[#6C7565] block mb-2">Email</span>
              <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email-input" className="w-full bg-transparent border-0 border-b-2 border-[#1A1F16] py-2 focus:outline-none focus:border-[#A67C00]" placeholder="you@home.in" />
            </label>
          </div>
          <label className="block mt-6">
            <span className="font-mono text-[10px] tracking-widest uppercase text-[#6C7565] block mb-2">Message</span>
            <textarea required rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message-input" className="w-full bg-transparent border-2 border-[#1A1F16] p-3 focus:outline-none focus:border-[#A67C00] resize-none" placeholder="How can we help?" />
          </label>
          <button type="submit" disabled={busy} data-testid="contact-submit-btn" className="mt-8 bg-[#1A1F16] text-[#F7F5F0] px-10 py-4 text-xs uppercase tracking-[0.25em] hover:bg-[#A67C00] disabled:opacity-50 transition-colors">
            {busy ? "Sending…" : "Send message"}
          </button>
        </form>
      </div>
    </section>
  );
}

function LandingFooter() {
  return (
    <footer className="border-t-2 border-[#1A1F16] bg-[#1A1F16] text-[#EAE7DD]">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
        <div>
          <div className="font-serif text-2xl tracking-tight">Fin<span className="italic text-[#A67C00]">·</span>Ledger</div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#6C7565] mt-2">A household ledger · Tracked in ₹ · Est. 2026</div>
          <div className="text-xs text-[#A8AFA0] mt-3" data-testid="footer-company-attribution">
            FinLedger is a product of <span className="text-[#A67C00]">SS Ayurvedam Digital Solutions</span>.
          </div>
        </div>
        <div className="flex gap-6 font-mono text-[10px] uppercase tracking-[0.2em]">
          <a href="#pricing" className="hover:text-[#A67C00] transition-colors">Pricing</a>
          <a href="#faq" className="hover:text-[#A67C00] transition-colors">FAQ</a>
          <a href="#contact" className="hover:text-[#A67C00] transition-colors">Contact</a>
          <Link to="/login" className="hover:text-[#A67C00] transition-colors" data-testid="footer-signin-link">Sign in</Link>
        </div>
      </div>
    </footer>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#F7F5F0] text-[#1A1F16] paper-grain" data-testid="landing-page">
      <LandingNav />
      <main>
        <Hero />
        <Features />
        <Screenshots />
        <PricingSection />
        <FAQ />
        <Contact />
      </main>
      <LandingFooter />
    </div>
  );
}
